/* SMA cart — single add-to-cart path for all custom cards (collection, home rows,
 * deals, wishlist, featured collections) + keeps every cart badge in sync.
 *
 * SMACart.add(formDataOrPayload, { source, blockCartDrawerOpening }) → Promise<cart>
 *  - Adds are queued (one request at a time) so rapid clicks on different cards
 *    don't hit the Shopify cart concurrently and fail.
 *  - Requests the cart-drawer section in the same call and dispatches the same
 *    `variant:add` + `cart:change` events the theme's product form does, so the
 *    drawer re-renders and opens with the correct line items and count.
 *  - Rejects with an Error whose message is Shopify's description (e.g. stock limit).
 *
 * Badges: any [data-cart-count] / .cart-count-bubble element is updated on every
 * `cart:change` (theme product form, drawer qty changes) and `cart:refresh`.
 */
(function () {
  if (window.SMACart) return;

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var SECTIONS = 'cart-drawer,variant-added';
  var queue = Promise.resolve();

  function setBadges(count) {
    count = parseInt(count, 10) || 0;
    document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach(function (el) {
      el.textContent = count > 0 ? count : '';
      el.setAttribute('data-cart-count', count);
    });
    document.querySelectorAll('.smah__cart').forEach(function (el) {
      el.setAttribute('aria-label', 'Cart, ' + count + ' items');
    });
  }

  function fetchCart() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  function doAdd(body, opts) {
    var init = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
    var variantId;

    if (body instanceof FormData) {
      variantId = body.get('id');
      body.set('sections', SECTIONS);
      body.set('sections_url', root + 'variants/' + variantId);
      init.body = body;
    } else {
      var payload = Object.assign({}, body);
      variantId = payload.items && payload.items[0] ? payload.items[0].id : payload.id;
      payload.sections = SECTIONS;
      payload.sections_url = root + 'variants/' + variantId;
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(payload);
    }

    return fetch(root + 'cart/add.js', init)
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (json) {
          if (!r.ok) throw new Error(json.description || json.message || 'Could not add to cart');
          return json;
        });
      })
      .then(function (added) {
        return fetchCart().then(function (cart) {
          cart.sections = added.sections;
          var items = added.items || [added];
          var target = opts.source && opts.source.isConnected ? opts.source : document;

          target.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: items, cart: cart, blockCartDrawerOpening: !!opts.blockCartDrawerOpening }
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cart }
          }));
          document.dispatchEvent(new CustomEvent('sma:cart:added', { bubbles: true, detail: items[0] }));
          return cart;
        });
      });
  }

  function add(body, opts) {
    var p = queue.then(function () { return doAdd(body, opts || {}); });
    queue = p.catch(function () {});
    return p;
  }

  document.addEventListener('cart:change', function (e) {
    if (e.detail && e.detail.cart) setBadges(e.detail.cart.item_count);
  });

  document.addEventListener('cart:refresh', function () {
    fetchCart().then(function (cart) { setBadges(cart.item_count); }).catch(function () {});
  });

  // Cart drawer "You may also like" / collection cards: their <form is="product-form"> gets
  // swapped in from a DOMParser document on every cart change and doesn't reliably upgrade,
  // so the theme handler never runs. Handle those submits here (capture phase, before the
  // theme's own listener, so it can never double-add).
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form' || !form.closest('#cart-drawer')) return;
    e.preventDefault();
    e.stopPropagation();

    var buttons = Array.prototype.slice.call(form.querySelectorAll('[type="submit"]'));
    if (buttons.some(function (b) { return b.disabled; })) return;
    var originals = buttons.map(function (b) { return b.innerHTML; });
    buttons.forEach(function (b) { b.disabled = true; b.setAttribute('aria-busy', 'true'); });

    add(new FormData(form), { source: form })
      .then(function () {
        buttons.forEach(function (b) { b.disabled = false; b.removeAttribute('aria-busy'); });
      })
      .catch(function (err) {
        console.warn('Add to cart failed:', err && err.message);
        buttons.forEach(function (b) { b.removeAttribute('aria-busy'); b.textContent = 'Try again'; });
        setTimeout(function () {
          buttons.forEach(function (b, i) { b.innerHTML = originals[i]; b.disabled = false; });
        }, 1800);
      });
  }, true);

  // Back/forward cache (e.g. returning from checkout) restores a stale header/drawer
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  });

  window.SMACart = { add: add, refreshBadges: function () { return fetchCart().then(function (c) { setBadges(c.item_count); return c; }); } };
})();
